import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  resetFilterData,
  setFilterDataField,
  setGridCurrentPage,
  setMaintenanceRequests,
} from "src/redux/slices/maintenanceRequestsSlice";
import { getFieldName, convertIsoStringToDate } from "src/utils/helpers";
import moment from "moment";
import { toast } from "react-toastify";
import useOnPageLeave from "src/hooks/useOnPageLeave";
import ToggleFilter from "src/components/ToggleFilter/ToggleFilter";
import useResize from "src/hooks/useResize";
import { useRef, useState, useCallback } from "react";
import FilterActionButtons from "src/components/FilterActionButtons/FilterActionButtons";
import {
  isSelectedFilterStatusIsCompleted,
  onShowCalendar,
  onHideCalendar,
} from "src/utils/common";
import {
  getServiceTerritories,
  setServiceTerritories,
} from "src/redux/slices/lookupSlice";
import { showHistoricalDataChangeSource } from "src/resources/enum";
import { setChangeHistoricalDataSource } from "src/redux/slices/sharedSlice";

const MaintenanceRequestsFilter = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["common", "registration", "dashboard"]);

  const creationDateFromRef = useRef<Calendar | null>(null);
  const creationDateToRef = useRef<Calendar | null>(null);

  const { isMobile } = useResize();
  const [isFilterShown, setIsFilterShown] = useState(!isMobile);

  const assignedBuildingData = useSelector(
    (state: any) => state.lookup.assignedBuildings
  );
  const {
    propertyId,
    sLABreached,
    systemServiceId,
    territoryId,
    cityId,
    status,
    startDate,
    endDate,
    managerSupervisorName,
    PRPNumber,
    PropertyTradeLicense,
  } = useSelector((state: any) => state.maintenanceRequests.filterData);
  const sLAOptions = [
    { label: t("exceeded"), value: true },
    { label: t("onTrack"), value: false },
  ];
  const systemNameOptions = useSelector((state: any) => state.lookup.systems);
  const serviceTerritories = useSelector(
    (state: any) => state.lookup.serviceTerritories
  );
  const citiesOptions = useSelector((state: any) => state.lookup.citiesOptions);
  const statuses = useSelector((state: any) => state.lookup.statusOptions);
  const language = useSelector((state: any) => state.lookup.language);

  const handleFormSubmit = (e: any) => {
    e.preventDefault();
  };

  const handleFilter = () => {
    goToGridFirstPage();
  };
  const handleFormFieldChange = (e: any) => {
    let name, value;

    if (e.target) {
      name = e.target.name;
      value = e.target.value;
    } else {
      name = e.name;
      value = e.value;
    }
    dispatch(setFilterDataField({ field: name, value }));
    if (name === "status") {
      dispatch(
        setChangeHistoricalDataSource(
          showHistoricalDataChangeSource.MaintenanceRequest
        )
      );
      dispatch(
        setFilterDataField({
          field: "showHistoricalData",
          value: isSelectedFilterStatusIsCompleted(statuses, value),
        })
      );
    }
    if (name == "startDate" && endDate) {
      var startDateValue = moment(value);
      var endDateValue = moment(endDate);
      if (startDateValue.isAfter(endDateValue)) {
        toast.error(t("startEndDateValidation"));
        dispatch(setFilterDataField({ field: "endDate", value }));
      }
    }
  };

  const handleCityChange = (e: any) => {
    handleFormFieldChange(e);
    dispatch(setServiceTerritories([]));
    dispatch(getServiceTerritories(false, e.value) as any);
  };

  const handleClearFilterForm = (): void => {
    dispatch(resetFilterData());
    goToGridFirstPage();
  };
  const goToGridFirstPage = useCallback(() => {
    dispatch(setGridCurrentPage(0));
  }, []);

  const onPageLeave = useCallback(() => {
    dispatch(resetFilterData());
    dispatch(setMaintenanceRequests({ data: [], totalRecords: 0 }));
  }, []);
  useOnPageLeave(onPageLeave);
  const toggleFilter = () => {
    setIsFilterShown(!isFilterShown);
  };
  const [viewDate, setViewDate] = useState<Date | null>(null);
  return (
    <>
      <ToggleFilter onClick={toggleFilter} />
      <div
        className={`px-2 pb-2 pt-3 maintenance-requests-filter ${
          !isMobile || isFilterShown ? "d-flex" : "d-none"
        } ${isMobile ? "my-3" : ""} `}
      >
        <p className="d-flex m-0 me-2 mb-2 ms-2 fit-content filter-by-label">
          {t("filterBy")}
        </p>
        <form onSubmit={handleFormSubmit}>
          <div className="filter-row mb-2">
            <Dropdown
              filter
              resetFilterOnHide
              emptyFilterMessage={t("noResultsFound")}
              options={assignedBuildingData}
              placeholder={t("filterByBuilding")}
              className="sla-dropdown"
              optionLabel="buildingName"
              optionValue="propertyId"
              panelClassName="status-dropdown-panel"
              onChange={handleFormFieldChange}
              value={propertyId}
              name="propertyId"
            />
            <Dropdown
              filter
              resetFilterOnHide
              emptyFilterMessage={t("noResultsFound")}
              options={sLAOptions}
              placeholder={t("sLABreached")}
              className="sla-dropdown"
              optionLabel="label"
              optionValue="value"
              panelClassName="status-dropdown-panel"
              onChange={handleFormFieldChange}
              value={sLABreached}
              name="sLABreached"
            />
            <Dropdown
              filter
              resetFilterOnHide
              emptyFilterMessage={t("noResultsFound")}
              options={systemNameOptions}
              placeholder={t("systemName")}
              className="system-name-dropdown"
              optionLabel={getFieldName(language)}
              panelClassName="status-dropdown-panel"
              onChange={handleFormFieldChange}
              optionValue="systemServiceId"
              value={systemServiceId}
              name="systemServiceId"
            />
            <Dropdown
              filter
              resetFilterOnHide
              emptyFilterMessage={t("noResultsFound")}
              options={citiesOptions}
              placeholder={t("city")}
              className="city-dropdown"
              optionLabel={getFieldName(language)}
              optionValue="cityId"
              panelClassName="status-dropdown-panel"
              onChange={handleCityChange}
              value={cityId}
              name="cityId"
            />
            <Dropdown
              filter
              resetFilterOnHide
              emptyFilterMessage={t("noResultsFound")}
              emptyMessage={t("noResultsFound")}
              options={cityId ? serviceTerritories : []}
              placeholder={t("territory") + " - " + t("selectCityFirstShort")}
              className="service-territory-dropdown"
              optionValue={"territoryId"}
              optionLabel={getFieldName(language)}
              panelClassName="status-dropdown-panel"
              onChange={handleFormFieldChange}
              value={territoryId}
              name="territoryId"
            />
            <Dropdown
              filter
              resetFilterOnHide
              emptyFilterMessage={t("noResultsFound")}
              options={statuses}
              placeholder={t("status")}
              className="status-dropdown"
              optionLabel={getFieldName(language)}
              optionValue="id"
              panelClassName="status-dropdown-panel"
              onChange={handleFormFieldChange}
              value={status}
              name="status"
            />
            <Calendar
              value={startDate}
              onChange={handleFormFieldChange}
              readOnlyInput
              placeholder={t("creationDateFrom")}
              name="startDate"
              dateFormat="dd/mm/yy"
              showIcon
              maxDate={new Date()}
              ref={creationDateFromRef}
              onHide={() => onHideCalendar(creationDateFromRef)}
              onShow={() => onShowCalendar(creationDateFromRef)}
              key={`startDate-${startDate?.toString()}`}
            />

            <Calendar
              value={endDate}
              onChange={handleFormFieldChange}
              readOnlyInput
              placeholder={t("creationDateTo")}
              name="endDate"
              dateFormat="dd/mm/yy"
              minDate={
                !startDate
                  ? new Date(-8640000000000000)
                  : convertIsoStringToDate(startDate)
              }
              ref={creationDateToRef}
              onHide={() => onHideCalendar(creationDateToRef)}
              onShow={() => onShowCalendar(creationDateToRef)}
              key={`endDate-${endDate?.toString()}`}
            />
            <InputText
              type="text"
              placeholder={t("managerSupervisorName")}
              onChange={handleFormFieldChange}
              value={managerSupervisorName}
              name="managerSupervisorName"
              autoComplete="off"
            />

             <InputText
              type="text"
              placeholder={t("PRPNumber")}
              onChange={handleFormFieldChange}
              value={PRPNumber}
              name="PRPNumber"
              autoComplete="off"
            />

             <InputText
              type="text"
              placeholder={t("propertyTradeLicense")}
              onChange={handleFormFieldChange}
              value={PropertyTradeLicense}
              name="PropertyTradeLicense"
              autoComplete="off"
            />

            
            <FilterActionButtons
              handleFilter={handleFilter}
              handleClear={handleClearFilterForm}
            />
          </div>
        </form>
      </div>
    </>
  );
};

export default MaintenanceRequestsFilter;
